import { EmptyState, ErrorState, LoadingState, WithHalfdot } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { EventCalendar } from "~/components/events/EventCalendar";
import { EventCard } from "~/components/events/EventCard";
import { MyEventsStrip } from "~/components/events/MyEventsStrip";
import { Screen } from "~/components/ui/Screen";
import { INTL_LOCALE, useLocale } from "~/i18n";
import { track } from "~/lib/analytics";
import { fetchEvents } from "~/lib/api";
import {
  dayKey,
  groupByDay,
  monthWindow,
  startOfDay,
  startOfMonth,
} from "~/lib/calendar";
import { queryKeys } from "~/lib/query-client";
import { useAuthStore } from "~/store/auth-store";
import { AnalyticsEventName } from "~/types/analytics";
import type { EventSummary } from "~/types/event";

/**
 * The page width at which the list moves beside the grid: the 420px calendar, the 40px
 * gap and a list no narrower than a phone's card, 360px.
 *
 * A container query on the page rather than a viewport breakpoint, because the side
 * rail takes its 264px out of the viewport first. A 13" iPad in portrait is 1032px
 * wide — "desktop" — yet leaves the page 688px, and splitting at `md` squeezed the
 * list into a 228px sliver beside the grid. A WebView without container queries
 * (Safari < 16) keeps the stacked layout, which works at every width.
 */
const SIDE_BY_SIDE = "@820";

/**
 * Events — the community calendar as a tab of its own.
 *
 * One request per month view, covering the whole grid so the padding days from the
 * neighbouring months get their dots too. The list beside the grid shows every event of
 * the month grouped under its day until the member picks a day, which narrows it to that
 * day alone; tapping the same day again lets the whole month back in. The events are
 * already in hand either way, so filtering costs no round-trip.
 *
 * Grid above list until the page is wide enough for both, then side by side — decided
 * in CSS, against the page's own width (see `SIDE_BY_SIDE`).
 */
export function EventsRoute() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const userId = useAuthStore((state) => state.user?.id ?? null);

  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState<Date | null>(null);

  const range = useMemo(() => monthWindow(month), [month]);
  const monthKey = `${month.getFullYear()}-${month.getMonth() + 1}`;

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: queryKeys.events(locale, monthKey),
    queryFn: ({ signal }) =>
      fetchEvents({ locale, from: range.from, to: range.to, signal }),
  });

  const events = useMemo(() => data ?? [], [data]);
  const byDay = useMemo(
    () => groupByDay(events, (event) => event.starts_at),
    [events],
  );
  const eventDays = useMemo(() => new Set(byDay.keys()), [byDay]);

  const intlLocale = INTL_LOCALE[locale];
  const dayFormat = new Intl.DateTimeFormat(intlLocale, { dateStyle: "long" });
  const monthLabel = new Intl.DateTimeFormat(intlLocale, {
    month: "long",
    year: "numeric",
  }).format(month);

  // The list: one group for the selected day, or the whole month in date order. Only
  // the days that belong to the month shown — the grid's padding cells are fetched for
  // their dots, but their events belong to the neighbouring month's list.
  const groups = useMemo((): {
    key: string;
    date: Date;
    items: EventSummary[];
  }[] => {
    if (selected) {
      const key = dayKey(selected);
      return [{ key, date: selected, items: byDay.get(key) ?? [] }];
    }
    return [...byDay.entries()]
      .map(([key, items]) => ({
        key,
        date: new Date(items[0].starts_at),
        items,
      }))
      .filter(({ date }) => date.getMonth() === month.getMonth())
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [selected, byDay, month]);

  const listIsEmpty = groups.every((group) => group.items.length === 0);

  /** Tapping the selected day again widens the list back to the month. */
  const selectDay = (day: Date) => {
    setSelected((current) => {
      const cleared = Boolean(current && dayKey(current) === dayKey(day));
      // A second tap widens the list back out, which is a deselect and not a filter.
      track(AnalyticsEventName.CalendarDaySelect, { props: { cleared } });
      return cleared ? null : startOfDay(day);
    });
  };

  /** Moving to another month drops the day filter; the day would not be on screen. */
  const changeMonth = (next: Date) => {
    track(AnalyticsEventName.CalendarMonthChange);
    setMonth(next);
    setSelected(null);
  };

  return (
    <Screen maxWidth="lg">
      <Box
        data-testid="events.page"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, md: 4 },
          // The container `SIDE_BY_SIDE` measures.
          containerType: "inline-size",
        }}
      >
        <Box>
          <Typography variant="OV1" sx={{ color: "text.muted" }}>
            {t("events.eyebrow")}
          </Typography>
          <Typography
            variant="H1"
            sx={{ color: "text.primary", marginTop: 0.5 }}
          >
            <WithHalfdot size={11}>{t("events.title")}</WithHalfdot>
          </Typography>
        </Box>

        {userId ? <MyEventsStrip userId={userId} locale={locale} /> : null}

        {isError ? (
          <ErrorState
            message={error instanceof Error ? error.message : t("events.error")}
            onRetry={() => void refetch()}
          />
        ) : (
          <Box
            sx={{
              display: "grid",
              // The grid is a fixed shape; the list takes the rest.
              gridTemplateColumns: { "@": "1fr", [SIDE_BY_SIDE]: "420px 1fr" },
              alignItems: "start",
              gap: { xs: 3, md: 5 },
            }}
          >
            <Box
              sx={{
                position: { [SIDE_BY_SIDE]: "sticky" },
                top: { [SIDE_BY_SIDE]: 48 },
              }}
            >
              <EventCalendar
                month={month}
                locale={locale}
                selected={selected}
                eventDays={eventDays}
                onSelect={selectDay}
                onMonthChange={changeMonth}
              />
            </Box>

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              data-testid="events.day-list"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  minHeight: 40,
                }}
              >
                <Typography
                  variant="H3"
                  sx={{
                    color: "text.primary",
                    // A bare month name opens with a capital; inside a full date
                    // Czech keeps it lowercase, so only the month title is transformed.
                    textTransform: selected ? "none" : "capitalize",
                  }}
                  data-testid="events.list-title"
                >
                  {selected ? dayFormat.format(selected) : monthLabel}
                </Typography>

                {selected ? (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setSelected(null)}
                    data-testid="events.show-month"
                    sx={{ flexShrink: 0 }}
                  >
                    {t("events.showMonth")}
                  </Button>
                ) : null}
              </Box>

              {isPending ? <LoadingState /> : null}

              {!isPending && listIsEmpty ? (
                <EmptyState
                  message={t(
                    selected ? "events.emptyDay" : "events.emptyMonth",
                  )}
                />
              ) : null}

              {groups.map((group) => (
                <Box
                  key={group.key}
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  data-testid={`events.group-${group.key}`}
                >
                  {/* A day heading only when the list spans the month; a single
                      day already has its date in the title above. */}
                  {!selected ? (
                    <Typography
                      variant="L1"
                      sx={{ color: "text.secondary", marginTop: 1 }}
                    >
                      {dayFormat.format(group.date)}
                    </Typography>
                  ) : null}

                  {group.items.map((event) => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Screen>
  );
}
